import { useEffect, useState } from 'react';
import * as api from '../api/client.js';

// ערים חרדיות זמינות ברירת מחדל (מהמנוע)
const CHAREIDI_CITIES = [
  { id: 'city_vilna', name: 'ווילנא', region: 'ליטא' },
  { id: 'city_boro_park', name: 'בארא פארק', region: 'ניו יורק' },
  { id: 'city_bnei_brak', name: 'בני ברק', region: 'ישראל' },
  { id: 'city_beitar', name: 'ביתר עלית', region: 'ישראל' },
  { id: 'city_modiin_illit', name: 'מודיעין עלית', region: 'ישראל' },
  { id: 'city_elad', name: 'אלעד', region: 'ישראל' },
  { id: 'city_buenos_aires', name: 'בואנוס איירס', region: 'ארגנטינה' },
];

const ETHNICITIES = [
  { value: 'ashkenazi', label: 'אשכנזי' },
  { value: 'sephardi', label: 'ספרדי' },
  { value: 'mizrahi', label: 'מזרחי' },
  { value: 'yemenite', label: 'תימני' },
];

const STREAMS = [
  { value: 'chassidic', label: 'חסידי' },
  { value: 'litvish', label: 'ליטאי' },
  { value: 'mizrahi', label: 'מזרחי' },
  { value: 'religious_zionist', label: 'דת"ל' },
];

export function JoinRoundDialog({
  roundId,
  onClose,
  onJoined,
}: {
  roundId: string;
  onClose: () => void;
  onJoined: () => void;
}) {
  const [dynastyName, setDynastyName] = useState('');
  const [ethnicity, setEthnicity] = useState('ashkenazi');
  const [stream, setStream] = useState('chassidic');
  const [homeCityId, setHomeCityId] = useState('city_bnei_brak');
  const [round, setRound] = useState<api.RoundSummary | null>(null);
  const [players, setPlayers] = useState<api.RoundPlayer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  const account = api.getAccount();
  const isCreator = round?.created_by === account?.id;
  const isJoined = players.some((p) => p.account_id === account?.id);

  const loadDetails = async (): Promise<void> => {
    try {
      const details = await api.getRound(roundId);
      setRound(details.round);
      setPlayers(details.players);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה');
    }
  };

  useEffect(() => {
    void loadDetails();
    const t = setInterval(() => void loadDetails(), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId]);

  const submit = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      await api.joinRound(roundId, {
        dynastyName,
        ethnicity,
        stream,
        homeCityId,
      });
      await loadDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה');
    } finally {
      setLoading(false);
    }
  };

  const start = async (): Promise<void> => {
    setStarting(true);
    setError(null);
    try {
      await api.startRound(roundId);
      onJoined();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה');
    } finally {
      setStarting(false);
    }
  };

  const takenCityIds = new Set(players.map((p) => p.home_city_id));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <h2>{round?.name ?? 'טוען...'}</h2>

        <div className="join-panels">
          <div className="join-form">
            {!isJoined ? (
              <>
                <h3>הצטרפות</h3>
                <label>
                  <span>שם החצר שלך</span>
                  <input
                    type="text"
                    value={dynastyName}
                    onChange={(e) => setDynastyName(e.target.value)}
                    placeholder="למשל: חצר האור"
                    disabled={loading}
                  />
                </label>

                <label>
                  <span>עדה</span>
                  <select value={ethnicity} onChange={(e) => setEthnicity(e.target.value)}>
                    {ETHNICITIES.map((e) => (
                      <option key={e.value} value={e.value}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>זרם</span>
                  <select value={stream} onChange={(e) => setStream(e.target.value)}>
                    {STREAMS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>עיר הבית</span>
                  <select value={homeCityId} onChange={(e) => setHomeCityId(e.target.value)}>
                    {CHAREIDI_CITIES.map((c) => (
                      <option
                        key={c.id}
                        value={c.id}
                        disabled={takenCityIds.has(c.id)}
                      >
                        {c.name} ({c.region}){takenCityIds.has(c.id) ? ' - תפוסה' : ''}
                      </option>
                    ))}
                  </select>
                </label>

                {error && <div className="error">{error}</div>}

                <button
                  className="primary"
                  onClick={() => void submit()}
                  disabled={loading || !dynastyName}
                >
                  {loading ? '...' : 'הצטרף'}
                </button>
              </>
            ) : (
              <>
                <h3>אתה במשחק</h3>
                <p>ממתין ליוצר המשחק להתחיל.</p>
                {isCreator && round?.status === 'waiting' && (
                  <button
                    className="primary"
                    onClick={() => void start()}
                    disabled={starting || players.length === 0}
                  >
                    {starting ? '...' : `🚀 התחל משחק (${players.length} שחקנים)`}
                  </button>
                )}
              </>
            )}
          </div>

          <div className="join-players">
            <h3>שחקנים ({players.length}/{round?.max_players ?? 0})</h3>
            {players.length === 0 ? (
              <div className="empty">עדיין אין שחקנים</div>
            ) : (
              <ul className="players-list">
                {players.map((p) => (
                  <li key={p.id}>
                    <strong>{p.dynasty_name}</strong>
                    <span className="meta">
                      {p.ethnicity} · {CHAREIDI_CITIES.find((c) => c.id === p.home_city_id)?.name ?? p.home_city_id}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>סגור</button>
        </div>
      </div>
    </div>
  );
}
